import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import type { FileMetadata, PaginatedData } from '../types'
import {
  filesService,
  type FileListParams,
  type CreateFileData,
  type UpdateFileData,
} from '../services/api/files'

export function useFiles(params?: FileListParams) {
  const serialized = params ? JSON.stringify(params) : 'default'
  const queryKey = useMemo(
    () => ['files', serialized],
    [serialized]
  )

  return useQuery({
    queryKey,
    queryFn: async (): Promise<PaginatedData<FileMetadata>> => {
      const result = await filesService.list(params)

      if (!result.success || !result.data) {
        throw new Error(
          result.message ?? 'Impossible de récupérer les fichiers'
        )
      }

      return result.data
    },
    staleTime: 2 * 60 * 1000,
  })
}

export function useFile(id: number) {
  return useQuery({
    queryKey: ['files', id],
    queryFn: async () => {
      const result = await filesService.getById(id)
      if (!result.success) {
        throw new Error(result.message ?? 'Fichier introuvable')
      }
      return result.data
    },
    enabled: Number.isFinite(id) && id > 0,
  })
}

export function useFilesByPatient(
  patientId: number,
  params?: Omit<FileListParams, 'patientId'>
) {
  const queryKey = useMemo(
    () => [
      'files',
      'patient',
      patientId,
      params ? JSON.stringify(params) : 'default',
    ],
    [patientId, params]
  )

  return useQuery({
    queryKey,
    queryFn: async (): Promise<PaginatedData<FileMetadata>> => {
      const result = await filesService.getByPatientId(patientId, params)

      if (!result.success || !result.data) {
        throw new Error(
          result.message ?? 'Impossible de récupérer les fichiers du patient'
        )
      }

      return result.data
    },
    enabled: Number.isFinite(patientId) && patientId > 0,
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateFileData) => {
      const result = await filesService.create(payload)

      if (!result.success || !result.data) {
        throw new Error(result.message ?? 'Création du fichier impossible')
      }

      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] })
      toast.success('Fichier créé avec succès')
    },
    onError: error => {
      const message =
        error instanceof Error ? error.message : "Une erreur s'est produite"
      toast.error(message)
    },
  })
}

export function useUpdateFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateFileData }) => {
      const result = await filesService.update(id, data)

      if (!result.success || !result.data) {
        throw new Error(result.message ?? 'Mise à jour du fichier impossible')
      }

      return result.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['files'] })
      queryClient.invalidateQueries({
        queryKey: ['files', variables.id],
      })
      toast.success('Fichier mis à jour')
    },
    onError: error => {
      const message =
        error instanceof Error ? error.message : "Une erreur s'est produite"
      toast.error(message)
    },
  })
}

export function useDeleteFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await filesService.delete(id)

      if (!result.success) {
        throw new Error(result.message ?? 'Suppression du fichier impossible')
      }

      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] })
      toast.success('Fichier supprimé')
    },
    onError: error => {
      const message =
        error instanceof Error ? error.message : "Une erreur s'est produite"
      toast.error(message)
    },
  })
}

export function useDownloadFile() {
  return useMutation({
    mutationFn: async (id: number) => {
      return await filesService.download(id)
    },
    onError: error => {
      const message =
        error instanceof Error ? error.message : "Une erreur s'est produite"
      toast.error(message)
    },
  })
}
